// SPDX-License-Identifier: GPL-3.0-or-later
// Stream background: the fixed values and the pure helpers. No DOM here.
// The game page may show the live stream of the operator, muted, while the channel is live.
//   "twitch": a panel BESIDE the table. Twitch refuses to autoplay a frame that something covers at
//             load, so the panel needs a free area with nothing painted above it.
//   "off": no frame, so the page makes no request to a player.
// Only the Twitch player is allowed. It is embedded with a frame. No script of a player is loaded.
// The YouTube choice is gone. An old saved "youtube" value loads as the default (see `normalizeSource`).

import { ROUTES } from "../src/shared/contracts.ts";

/**
 * The kill switch of the stream background. It is a deploy-time value. `false` means: the page never
 * shows a stream and never asks `/api/live`. Change it here and deploy (see RUNBOOK.md).
 */
export const STREAM_BACKGROUND_ENABLED = true;

/** The route that tells if the Twitch channel is live. */
export const LIVE_ROUTE = ROUTES.live;

/** The page asks the live route this often while it is visible (ms). */
export const LIVE_POLL_MS = 60_000;

/**
 * The Twitch channel. The operator moved it from "sigmashake" to "watchsigma".
 * Source: sigmashake-web WatchSigma page ("Twitch is now twitch.tv/watchsigma") and
 * sigmashake-obs/config/channel-state.json.
 */
export const TWITCH_CHANNEL = "watchsigma";

/** The two choices. "off" removes the frame. */
export const STREAM_SOURCES = ["twitch", "off"] as const;

/** One choice. */
export type StreamSource = (typeof STREAM_SOURCES)[number];

/**
 * The source that the layer shows when the page gives none, or an old value that is no choice now.
 * The panel shows only on a wide screen with a mouse, so a narrow screen shows no stream.
 * The choice of the player is saved by the settings store (`streamBackground`), not here.
 */
export const DEFAULT_STREAM_SOURCE: StreamSource = "twitch";

/** The Twitch player origin. It must match `frame-src` in the CSP. */
export const TWITCH_PLAYER_ORIGIN = "https://player.twitch.tv";

/** The smallest size of a Twitch player, in CSS pixels. */
export const MIN_FRAME_WIDTH = 400;
export const MIN_FRAME_HEIGHT = 300;

/** The ratio of the frame: width over height. */
export const FRAME_ASPECT = 16 / 9;

/** The Twitch panel shows only when the viewport is at least this wide (CSS pixels). */
export const PANEL_MIN_VIEWPORT_WIDTH = 1280;

/** The gap between the table and the panel, and between the panel and the playfield edge. */
export const PANEL_GUTTER = 8;

/** The panel is never wider than this. */
export const PANEL_MAX_WIDTH = 640;

/**
 * The drawn table is about 0.507 times as wide as the playfield is high (measured at 1920x1080,
 * 1440x900 and 1280x800). The value here is a little larger, so the estimate of the table is never
 * narrower than the real table and the panel never covers it.
 */
export const TABLE_WIDTH_PER_HEIGHT = 0.53;

/** The box of the Twitch panel, in CSS pixels, from the top left corner of the playfield. */
export interface PanelBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** True when the value is one of the two choices. */
export function isStreamSource(value: unknown): value is StreamSource {
  return typeof value === "string" && (STREAM_SOURCES as readonly string[]).includes(value);
}

/**
 * The choice that a saved value gives. "off" stays "off". Any other value gives the default. This
 * includes the removed "youtube" value and a value that is no string.
 */
export function normalizeSource(value: unknown): StreamSource {
  return value === "off" ? "off" : DEFAULT_STREAM_SOURCE;
}

/** The URL of the Twitch player. The parent must be the host name of the page. */
export function twitchUrl(channel: string, hostname: string): string {
  return (
    `${TWITCH_PLAYER_ORIGIN}/?channel=${encodeURIComponent(channel)}` +
    `&parent=${encodeURIComponent(hostname)}&muted=true&autoplay=true`
  );
}

/** Decide what the page can show. Twitch with no host name shows nothing, because Twitch refuses a frame with no parent. */
export function resolveSource(requested: StreamSource, hostname: string): StreamSource {
  return requested === "twitch" && hostname === "" ? "off" : requested;
}

/** The URL of a source, or `null` for "off". */
export function sourceUrl(source: StreamSource, hostname: string): string | null {
  return source === "twitch" ? twitchUrl(TWITCH_CHANNEL, hostname) : null;
}

function validDimension(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * The box of the Twitch panel, or `null` when it does not fit. The panel sits in the free area to
 * the right of the table, `PANEL_GUTTER` from the table and from the right edge, at the top of the
 * playfield. The box is at least 400 by 300 and at most 640 wide. The video is 16:9 inside the box.
 * The table is centred in the playfield. Its width is an estimate (see `TABLE_WIDTH_PER_HEIGHT`).
 * A viewport narrower than 1280 px never gets a panel.
 */
export function panelLayout(
  viewportWidth: number,
  playfieldWidth: number,
  playfieldHeight: number,
): PanelBox | null {
  const pw = validDimension(playfieldWidth);
  const ph = validDimension(playfieldHeight);
  if (validDimension(viewportWidth) < PANEL_MIN_VIEWPORT_WIDTH) return null;
  const tableWidth = Math.min(pw, Math.ceil(ph * TABLE_WIDTH_PER_HEIGHT));
  const left = Math.ceil(pw / 2 + tableWidth / 2 + PANEL_GUTTER);
  const width = Math.min(PANEL_MAX_WIDTH, Math.floor(pw - left - PANEL_GUTTER));
  const height = Math.max(MIN_FRAME_HEIGHT, Math.ceil(width / FRAME_ASPECT));
  if (width < MIN_FRAME_WIDTH || height > ph - 2 * PANEL_GUTTER) return null;
  return { left, top: PANEL_GUTTER, width, height };
}
